import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CompanyDetailsTab, ClientQuotesTab, ClientJobsTab, ClientDocumentsTab, ClientPersonaTab, } from "./components";
import { PageHeader, PageHeaderWithBack, useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { contactsService } from "@/api/services/contacts.service";
import { getHomeRoute } from "@/utils/navigation";
import styles from "./ContactEdit.module.css";
const TABS = [
    { id: "company", label: "Company Details" },
    { id: "quotes", label: "Client Quotes" },
    { id: "jobs", label: "Client Jobs" },
    { id: "documents", label: "Client Documents" },
    { id: "persona", label: "Client Persona" },
];
export const ContactEdit = () => {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const { user, switchToClientView } = useAuth();
    const { success, error: showError } = useToast();
    const [activeTab, setActiveTab] = useState("company");
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const fetchContact = useCallback(async (companyContactsPage, companyContactsPageSize) => {
        if (!contactId)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await contactsService.getContact(contactId, companyContactsPage, companyContactsPageSize);
            setContact(response.data);
        }
        catch (err) {
            console.error("Error fetching contact:", err);
            setError("Failed to load contact details");
        }
        finally {
            setLoading(false);
        }
    }, [contactId]);
    useEffect(() => {
        fetchContact();
    }, [fetchContact, contactId]);
    const handleBackClick = () => {
        navigate(-1);
    };
    const handleSave = async (data) => {
        if (!contactId)
            return;
        try {
            setSaving(true);
            setError(null);
            const response = await contactsService.updateContact(contactId, data);
            setContact(response.data);
            success("Contact saved successfully!");
        }
        catch (err) {
            console.error("Error saving contact:", err);
            setError("Failed to save contact");
            showError("Failed to save contact. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleViewPortal = async () => {
        if (!contactId)
            return;
        try {
            const response = await contactsService.switchAccount(contactId);
            const { token, user: portalUser } = response.data;
            // Update authentication context with the switched account
            switchToClientView(token, {
                id: portalUser.id,
                email: portalUser.email,
                name: portalUser.name || portalUser.email,
                role: portalUser.role,
            });
            // Defer navigation so auth state is committed before route render
            queueMicrotask(() => navigate(getHomeRoute(portalUser.role)));
        }
        catch (err) {
            console.error("Error switching account:", err);
            showError("Failed to switch to contact portal");
        }
    };
    const renderTabContent = () => {
        if (loading) {
            return (_jsx("div", { className: styles.loadingState, children: _jsx("p", { children: "Loading contact details..." }) }));
        }
        if (error) {
            return (_jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: () => fetchContact(), className: styles.retryBtn, children: "Retry" })] }));
        }
        if (!contact) {
            return (_jsx("div", { className: styles.errorState, children: _jsx("p", { children: "Contact not found" }) }));
        }
        switch (activeTab) {
            case "company":
                return (_jsx(CompanyDetailsTab, { contact: contact, onSave: handleSave, saving: saving, onRefetchContact: fetchContact }));
            case "quotes":
                return _jsx(ClientQuotesTab, { companyName: contact.companyName });
            case "jobs":
                return _jsx(ClientJobsTab, { companyName: contact.companyName });
            case "documents":
                return _jsx(ClientDocumentsTab, {});
            case "persona":
                return _jsx(ClientPersonaTab, { contactId: contactId });
            default:
                return null;
        }
    };
    // const getContactTypeBadge = () => {
    //   if (!contact) return "Customer";
    //   const type = contact.type;
    //   return type.charAt(0).toUpperCase() + type.slice(1);
    // };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Edit Contact", onBack: handleBackClick, breadcrumb: "Contacts/Edit Contact", actions: _jsx("button", { className: styles.actionBtn, onClick: handleViewPortal, children: "View Portal" }) }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: TABS.map((tab) => (_jsx("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), role: "tab", "aria-selected": activeTab === tab.id, children: tab.label }, tab.id))) }) }), _jsx("div", { className: `${styles.contentArea} ${activeTab === "company" ||
                    activeTab === "quotes" ||
                    activeTab === "persona"
                    ? styles.contentAreaNarrow
                    : ""}`, children: renderTabContent() })] }));
};
